<template>
  <div class="blog-overview--wrap">
    <v-container class="my-10">
      <div class="d-flex align-center flex-wrap mb-10">
        <h1 class="text-h4 font-weight-medium mb-4 mr-auto">
          Gesundheitsthemen
        </h1>

        <blog-filters
          v-model:active-tag-filters="filters"
          v-model:search="search"
          :tags="tags"
        />
      </div>

      <v-row v-if="paginatedEntries.length > 0" class="mb-10">
        <v-col
          v-for="entry in paginatedEntries"
          :key="entry.id"
          :cols="12"
          :sm="6"
          :lg="4"
          class="d-flex flex-column"
        >
          <blog-list-entry
            :entry="entry"
            class="mb-4"
            @show:details="entry => blogEntryToShow = entry"
          />
        </v-col>
      </v-row>

      <v-alert v-else color="primary">
        Keine Einträge vorhanden.
      </v-alert>

      <v-pagination
        v-if="totalPages > 1"
        v-model="page"
        :length="totalPages"
      />
    </v-container>

    <blog-dialog
      :entry="blogEntryToShow"
      @close="blogEntryToShow = null"
    />

    <!--
    ---- Only used to help nuxt(img) detect the images within the optional
    ---- dialogs which aren't present in the DOM while building the static
    ---- pages.
    --->
    <div class="d-none" aria-hidden="true">
      <template v-for="entry in blogEntries" :key="entry.id">
        <cms-block-renderer
          v-if="Array.isArray(entry.blocks)"
          :blocks="entry.blocks"
        />
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'blog-overview',

  setup () {
    const { blogEntries } = useBlogPage()
    return { blogEntries }
  },

  data () {
    return {
      page: 1,
      itemsPerPage: 9,
      filters: [],
      search: '',
      blogEntryToShow: null,
    }
  },

  computed: {
    /**
     * A blog-entry may have tags. We want to give the user the possibility to
     * filter blog-entries by those. So we collect all unique tags to be able
     * to create a select with those.
     *
     * @returns {array}
     */
    tags () {
      const uniqueTags = new Map()

      this.blogEntries.forEach(({ tags }) => {
        tags.forEach(tag => {
          uniqueTags.set(tag.id, tag)
        })
      })

      return [...uniqueTags.values()]
    },

    /**
     * Blog-entries filtered by the tags selected, search-term entered by the user.
     *
     * @returns {array}
     */
    filteredEntries () {
      const filteredByTags = this.filterByTags(this.blogEntries)
      const filteredByTagsAndSearch = this.filterBySearchTerm(filteredByTags)

      return filteredByTagsAndSearch.sort((a, b) => {
        if (!a.date && b.date) return 1
        if (a.date && !b.date) return -1

        return b.date.localeCompare(a.date)
      })
    },

    /**
     * Items for the current page after filters have been applied.
     *
     * @returns {array}
     */
    paginatedEntries () {
      const from = (this.page - 1) * this.itemsPerPage
      const to = from + this.itemsPerPage

      return this.filteredEntries.slice(from, to)
    },

    /**
     * @returns {number}
     */
    totalPages () {
      return Math.ceil(this.filteredEntries.length / this.itemsPerPage)
    },
  },

  watch: {
    filteredEntries () {
      this.page = 1
    },

    page () {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  },

  methods: {
    /**
     * Filters given entries by the selected tags.
     *
     * @param {array} entries
     * @returns {array}
     */
    filterByTags (entries) {
      if (this.filters.length === 0) {
        return entries
      }

      return entries.filter(({ tags }) =>
        tags.some(({ id }) => this.filters.includes(id))
      )
    },

    /**
     * Filters given entries by the entered search-term.
     *
     * @param {array} entries
     * @returns {array}
     */
    filterBySearchTerm (entries) {
      if (!this.search) {
        return entries
      }

      const query = this.search.toLowerCase()

      return entries.filter(({ title, teaser }) =>
        title.toLowerCase().includes(query) || teaser.toLowerCase().includes(query)
      )
    },
  },
}
</script>
